import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Film, Users, Target, HelpCircle, ArrowRight, Linkedin, Globe, User, Twitter, Instagram, Youtube, ExternalLink, Crown } from "lucide-react";
import SEO from "../components/SEO";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function About() {
  const [founders, setFounders] = useState([]);
  const [boardMembers, setBoardMembers] = useState([]);

  useEffect(() => {
    const fetchFounders = async () => {
      try {
        const response = await axios.get(`${API}/api/founders`);
        setFounders(response.data);
      } catch (error) {
        console.error("Failed to fetch founders:", error);
      }
    };
    const fetchBoardMembers = async () => {
      try {
        const response = await axios.get(`${API}/api/board-members`);
        setBoardMembers(response.data);
      } catch (error) {
        console.error("Failed to fetch board members:", error);
      }
    };
    fetchFounders();
    fetchBoardMembers();
  }, []);

  const faqs = [
    {
      question: "What is AI Film?",
      answer: "AI film refers to movies, shorts, and video content created using artificial intelligence tools. This includes AI-generated imagery, scripts written with AI assistance, AI voice synthesis, and fully AI-generated video content."
    },
    {
      question: "Why a society?",
      answer: "The AI Film Society exists to unite creators, establish industry standards, share knowledge, and advocate for ethical AI filmmaking practices. We believe in building a supportive community as this art form evolves."
    },
    {
      question: "Who can join?",
      answer: "Anyone interested in AI filmmaking can join! Whether you're a seasoned filmmaker exploring AI tools, a technologist passionate about cinema, or simply curious about the future of storytelling—there's a place for you here."
    },
    {
      question: "How do I submit my AI film?",
      answer: "Visit the Submit page in our navigation menu to share your AI-generated film with the community. Submissions are reviewed by our team and approved films will be showcased in our Films gallery. This is for community display—for festival submissions, check each festival's individual requirements on our Festivals page."
    }
  ];

  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8">
      <SEO 
        title="About" 
        description="Learn about the AI Film Society - our mission to unite AI filmmakers, establish industry standards, and build a supportive community for the future of cinema."
      />
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-16"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-purple-light mb-4 font-mono">
            Our Story
          </p>
          <h1 className="font-syne text-4xl md:text-6xl font-bold text-white mb-6">
            About AI Film Society
          </h1>
        </motion.div>

        {/* Mission */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-16 h-16 bg-purple/20 flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-purple-light" />
              </div>
              <h2 className="font-syne text-3xl font-bold text-white mb-6">
                Our Mission
              </h2>
              <p className="text-lg text-slate-300 leading-relaxed mb-6">
                To foster the growth of AI-generated cinema by connecting filmmakers, 
                sharing knowledge, and establishing ethical standards for this emerging art form.
              </p>
              <p className="text-slate-400">
                We believe AI filmmaking is not a shortcut, but a new instrument. 
                We exist to unite the pioneers, debate the ethics, and define the craft 
                of this new era. We are here to ensure the future of film remains 
                human-led, even if the tools are synthetic.
              </p>
            </div>
            <div className="aspect-square relative overflow-hidden flex items-center justify-center">
              <img 
                src="/images/logo-white.png"
                alt="AI Film Society Logo"
                className="w-4/5 h-4/5 object-contain"
              />
            </div>
          </div>
        </motion.section>

        {/* Founders */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <h2 className="font-syne text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <Crown className="w-6 h-6 text-purple-light" />
            {founders.length > 1 ? "Founders" : "Founder"}
          </h2>
          {founders.length > 0 ? (
            <div className="space-y-6">
              {founders.map((founder, index) => (
                <motion.div
                  key={founder.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`founder-${index}`}
                  className="p-8 md:p-12 bg-void-surface border border-white/5"
                >
                  <div className="flex flex-col md:flex-row gap-8 items-center">
                    <div className="flex-shrink-0">
                      {founder.image_url ? (
                        <img 
                          src={founder.image_url}
                          alt={founder.name}
                          className="w-32 h-32 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-32 h-32 rounded-full bg-purple/20 flex items-center justify-center">
                          <Crown className="w-16 h-16 text-purple-light" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-xs uppercase tracking-wider text-purple-light mb-2 font-mono">
                        {founder.title || "Founder"}
                      </p>
                      <h3 className="font-syne text-2xl font-bold text-white mb-4">
                        {founder.name}
                      </h3>
                      {founder.bio && (
                        <p className="text-slate-300 mb-4">
                          {founder.bio}
                        </p>
                      )}
                      {/* Social Links */}
                      {(founder.website_url || founder.linkedin_url || founder.twitter_url || founder.instagram_url || founder.youtube_url || founder.imdb_url) && (
                        <div className="flex items-center gap-4 pt-4 border-t border-white/5">
                          {founder.website_url && (
                            <a
                              href={founder.website_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-slate-400 hover:text-purple-light transition-colors"
                              aria-label={`${founder.name}'s Website`}
                            >
                              <Globe className="w-5 h-5" />
                            </a>
                          )}
                          {founder.linkedin_url && (
                            <a
                              href={founder.linkedin_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-slate-400 hover:text-purple-light transition-colors"
                              aria-label={`${founder.name}'s LinkedIn`}
                            >
                              <Linkedin className="w-5 h-5" />
                            </a>
                          )}
                          {founder.twitter_url && (
                            <a
                              href={founder.twitter_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-slate-400 hover:text-purple-light transition-colors"
                              aria-label={`${founder.name}'s Twitter`}
                            >
                              <Twitter className="w-5 h-5" />
                            </a>
                          )}
                          {founder.instagram_url && (
                            <a
                              href={founder.instagram_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-slate-400 hover:text-purple-light transition-colors"
                              aria-label={`${founder.name}'s Instagram`}
                            >
                              <Instagram className="w-5 h-5" />
                            </a>
                          )}
                          {founder.youtube_url && (
                            <a
                              href={founder.youtube_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-slate-400 hover:text-purple-light transition-colors"
                              aria-label={`${founder.name}'s YouTube`}
                            >
                              <Youtube className="w-5 h-5" />
                            </a>
                          )}
                          {founder.imdb_url && (
                            <a
                              href={founder.imdb_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-slate-400 hover:text-purple-light transition-colors"
                              aria-label={`${founder.name}'s IMDb`}
                            >
                              <ExternalLink className="w-5 h-5" />
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="p-8 md:p-12 bg-void-surface border border-white/5">
              <div className="flex flex-col md:flex-row gap-8 items-center">
                <div className="flex-shrink-0">
                  <img 
                    src="/images/profile-default.png"
                    alt="Sam Joos"
                    className="w-32 h-32 rounded-full object-cover"
                  />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-purple-light mb-2 font-mono">
                    Founder
                  </p>
                  <h3 className="font-syne text-2xl font-bold text-white mb-4">
                    Sam Joos
                  </h3>
                  <p className="text-slate-300">
                    Sam is an award-winning commercial director and cinematographer with two decades 
                    of experience in film and advertising. He founded AI Film Society to create a home 
                    for filmmakers working with artificial intelligence and to help define the craft 
                    of cinema in this new era.
                  </p>
                </div>
              </div>
            </div>
          )}
        </motion.section>

        {/* Board & Advisors */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <h2 className="font-syne text-2xl font-bold text-white mb-6">
            Board & Advisors
          </h2>
          {boardMembers.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {boardMembers.map((member, index) => (
                <motion.div
                  key={member.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`board-member-${index}`}
                  className="p-6 bg-void-surface border border-white/5 hover:border-purple/30 transition-all"
                >
                  <div className="flex items-start gap-4">
                    {member.image_url ? (
                      <img 
                        src={member.image_url} 
                        alt={member.name}
                        className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-purple/20 flex items-center justify-center flex-shrink-0">
                        <User className="w-8 h-8 text-purple-light" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-syne font-bold text-white text-lg truncate">
                        {member.name}
                      </h3>
                      <p className="text-purple-light text-sm">{member.role}</p>
                      {member.title && (
                        <p className="text-slate-400 text-sm">{member.title}</p>
                      )}
                    </div>
                  </div>
                  {member.bio && (
                    <p className="text-slate-400 text-sm mt-4 line-clamp-3">
                      {member.bio}
                    </p>
                  )}
                  {(member.linkedin_url || member.website_url) && (
                    <div className="flex items-center gap-3 mt-4 pt-4 border-t border-white/5">
                      {member.linkedin_url && (
                        <a
                          href={member.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-slate-400 hover:text-purple-light transition-colors"
                          aria-label={`${member.name}'s LinkedIn`}
                        >
                          <Linkedin className="w-5 h-5" />
                        </a>
                      )}
                      {member.website_url && (
                        <a
                          href={member.website_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-slate-400 hover:text-purple-light transition-colors"
                          aria-label={`${member.name}'s Website`}
                        >
                          <Globe className="w-5 h-5" />
                        </a>
                      )}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="p-8 bg-void-highlight border border-white/5 text-center">
              <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">
                Board and advisory positions coming soon. Interested in contributing? 
                <a href="mailto:sam@aiadstudio.com?subject=AI Film Society Board Interest" className="text-purple-light hover:text-white ml-1">
                  Get in touch →
                </a>
              </p>
            </div>
          )}
        </motion.section>

        {/* FAQ */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <div className="flex items-center gap-3 mb-8">
            <HelpCircle className="w-6 h-6 text-purple-light" />
            <h2 className="font-syne text-2xl font-bold text-white">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`faq-${index}`}
                className="p-6 bg-void-surface border border-white/5"
              >
                <h3 className="font-syne font-bold text-white text-lg mb-3">
                  {faq.question}
                </h3>
                <p className="text-slate-400">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <h2 className="font-syne text-3xl font-bold text-white mb-6">
            Ready to Join?
          </h2>
          <p className="text-slate-300 mb-8 max-w-xl mx-auto">
            Become part of the community shaping the future of AI cinema.
          </p>
          <Link
            to="/join"
            data-testid="about-join-cta"
            className="inline-flex items-center gap-2 px-8 py-4 bg-purple text-white font-bold uppercase tracking-widest text-xs rounded-full hover:bg-purple-hover transition-all"
            onClick={() => window.scrollTo(0, 0)}
          >
            Join the Society
            <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
